import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {isEqual} from 'lodash'
import VideoControl from '../../components/cms/video-control'
import ModuleContainer from '../../components/cms/module-container'
import heroSlotRecipe from './index.recipe'
import HeroContent from './content'
import {StylesProvider} from './context'
import HeroAsset from './asset'
import {hasValidVideoContent, normalizeAsset} from '../../utils/utils'
import useVideoControl from '../../hooks/useVideoControl'

/**
 * Hero module with asset (image or video), heading, copy, and buttons
 *
 * @component
 * @param {string} [anchor] - Optional anchor ID for the module
 * @param {object} [assetMobile] - Optional asset object for mobile devices
 * @param {object} [assetDesktop] - Optional asset object for desktop devices
 * @param {'default'|'subtle'|'strong'|'inverse'} [background='default'] - Background style for the module
 * @param {number} [headingLevel] - HTML heading level (1-6) for the main heading
 * @param {string} headline - Headline text
 * @param {object} [copy] - Copy object
 * @param {object} [primaryCta] - Primary call-to-action object
 * @param {object} [secondaryCta] - Secondary call-to-action object
 * @param {object} [priceProduct] - Price object with optional label
 * @param {'center-center'|'center-left'|'bottom-left'} [textPosition] - Position of the text content over the asset
 * @param {'0%'|'40%'|'80%'} [darken] - Darken overlay percentage on the asset
 * @returns {JSX.Element} Rendered Hero module
 */
const Hero = ({
    moduleId,
    moduleType,
    pageId,
    anchor,
    assetMobile,
    assetDesktop,
    background,
    headingLevel = 2,
    headline,
    copy,
    primaryCta,
    secondaryCta,
    priceProduct,
    textPosition = 'center-center', // 'center-center', 'center-left', 'bottom-left'
    darken = '0%' // '0%', '40%', '80%'
}) => {
    const isMobileVideo = hasValidVideoContent(assetMobile)
    const isDesktopVideo = hasValidVideoContent(assetDesktop)

    const mobileVideoRef = React.useRef(null)
    const desktopVideoRef = React.useRef(null)
    const [isMobilePlaying, toggleMobile] = useVideoControl(assetMobile?.autoplay, mobileVideoRef)
    const [isDesktopPlaying, toggleDesktop] = useVideoControl(
        assetDesktop?.autoplay,
        desktopVideoRef
    )

    // Handle video play/pause control
    const onSingleToggle = () => {
        const anyPlaying = isMobilePlaying || isDesktopPlaying
        if (anyPlaying) {
            if (isMobilePlaying) toggleMobile()
            if (isDesktopPlaying) toggleDesktop()
        } else {
            toggleMobile()
            toggleDesktop()
        }
    }

    const recipe = useSlotRecipe({recipe: heroSlotRecipe})
    const styles = useMemo(() => recipe({textPosition, darken}), [recipe, textPosition, darken])

    // Show custom video control when videoControls is false (or undefined)
    // When videoControls is true, native controls are shown instead
    const showMobileVideoControl = isMobileVideo && assetMobile?.type === 'direct'
    const showDesktopVideoControl = isDesktopVideo && assetDesktop?.type === 'direct'

    // When assets are identical after normalization (ignoring insignificant differences),
    // render single video control
    const normalizedMobile = normalizeAsset(assetMobile)
    const normalizedDesktop = normalizeAsset(assetDesktop)
    const assetsAreIdentical =
        normalizedMobile && normalizedDesktop && isEqual(normalizedMobile, normalizedDesktop)
    const showSingleVideoControl =
        assetsAreIdentical && (showMobileVideoControl || showDesktopVideoControl)

    return (
        // always use dark theme
        <div className="chakra-theme" data-theme="dark">
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                css={styles.container}
                spacing="none"
            >
                <StylesProvider value={styles}>
                    <HeroAsset
                        normalizedMobile={normalizedMobile}
                        normalizedDesktop={normalizedDesktop}
                        mobileVideoRef={mobileVideoRef}
                        desktopVideoRef={desktopVideoRef}
                    />

                    <HeroContent
                        headingLevel={headingLevel}
                        headline={headline}
                        copy={copy}
                        primaryCta={primaryCta}
                        secondaryCta={secondaryCta}
                        priceProduct={priceProduct}
                    />

                    {/* Video control - bottom right, responsive based on asset type */}
                    {showSingleVideoControl ? (
                        <Box position="absolute" bottom={6} right={6} zIndex={1}>
                            <VideoControl
                                isPlaying={isMobilePlaying || isDesktopPlaying}
                                onToggle={onSingleToggle}
                            />
                        </Box>
                    ) : (
                        (showMobileVideoControl || showDesktopVideoControl) && (
                            <>
                                {/* Mobile video control */}
                                {showMobileVideoControl && (
                                    <Box
                                        position="absolute"
                                        bottom={6}
                                        right={6}
                                        zIndex={1}
                                        display={{base: 'block', s: 'none'}}
                                    >
                                        <VideoControl
                                            isPlaying={isMobilePlaying}
                                            onToggle={toggleMobile}
                                        />
                                    </Box>
                                )}
                                {/* Desktop video control */}
                                {showDesktopVideoControl && (
                                    <Box
                                        position="absolute"
                                        bottom={6}
                                        right={6}
                                        zIndex={1}
                                        display={{base: 'none', s: 'block'}}
                                    >
                                        <VideoControl
                                            isPlaying={isDesktopPlaying}
                                            onToggle={toggleDesktop}
                                        />
                                    </Box>
                                )}
                            </>
                        )
                    )}
                </StylesProvider>
            </ModuleContainer>
        </div>
    )
}

const assetPropType = PropTypes.shape({
    type: PropTypes.oneOf(['image', 'direct', 'brightcove']),
    // Image props
    src: PropTypes.string,
    alt: PropTypes.string,
    // Video common props
    title: PropTypes.string,
    poster: PropTypes.object,
    autoplay: PropTypes.bool,
    videoControls: PropTypes.bool,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    // Direct video props
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string,
            type: PropTypes.string,
            breakpoint: PropTypes.string
        })
    ),
    // Brightcove video props
    brightcoveId: PropTypes.string
})

Hero.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    assetMobile: assetPropType,
    assetDesktop: assetPropType,
    headingLevel: PropTypes.number,
    headline: PropTypes.string,
    copy: PropTypes.shape({
        text: PropTypes.string
    }),
    primaryCta: PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.shape({
            type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
            value: PropTypes.string
        }),
        openInNewTab: PropTypes.bool
    }),
    secondaryCta: PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.shape({
            type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
            value: PropTypes.string
        }),
        openInNewTab: PropTypes.bool
    }),
    priceProduct: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
        label: PropTypes.string
    }),
    textPosition: PropTypes.oneOf(['center-center', 'center-left', 'bottom-left']),
    darken: PropTypes.oneOf(['0%', '40%', '80%']),
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string
}

export default Hero
